import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BarChart3, TrendingUp, Star, Eye, ChevronRight } from 'lucide-react';
import { API } from '../utils/helpers';

const StatsWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/user/stats`, { credentials: 'include' })
      .then(res => res.json())
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="glass-card p-6 mb-8 animate-pulse h-32" />;
  if (!stats || stats.summary?.total_readings === 0) return null;

  const { summary, top_cards } = stats;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className="glass-card p-6 mb-8 border border-gold-300/10"
      data-testid="stats-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold gold-text flex items-center gap-2">
          <BarChart3 className="w-5 h-5" /> Tu Actividad
        </h2>
        <Link 
          to="/stats" 
          className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
          data-testid="stats-view-all"
        >
          Ver todo <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <div className="p-3 rounded-xl bg-white/5 text-center">
          <p className="text-2xl font-bold text-purple-400">{summary.total_readings}</p>
          <p className="text-xs text-[var(--text-muted)]">Lecturas</p>
        </div>
        <div className="p-3 rounded-xl bg-white/5 text-center">
          <p className="text-2xl font-bold text-purple-400">{summary.total_cards_drawn}</p>
          <p className="text-xs text-[var(--text-muted)]">Cartas</p>
        </div>
        <div className="p-3 rounded-xl bg-white/5 text-center">
          <p className="text-2xl font-bold text-purple-400">{summary.horoscopes_viewed}</p>
          <p className="text-xs text-[var(--text-muted)]">Horoscopos</p>
        </div>
        <div className="p-3 rounded-xl bg-white/5 text-center">
          <p className="text-sm font-bold text-purple-300 truncate">{summary.favorite_spread}</p>
          <p className="text-xs text-[var(--text-muted)]">Tirada Favorita</p>
        </div>
      </div>

      {/* Top Cards Preview */}
      {top_cards && top_cards.length > 0 && (
        <div>
          <p className="text-xs text-[var(--text-muted)] mb-2">Cartas más frecuentes</p>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {top_cards.slice(0, 5).map((card, i) => (
              <div key={card.id} className="flex-shrink-0 text-center">
                {card.image ? (
                  <img 
                    src={card.image} 
                    alt={card.name} 
                    className="w-12 h-18 object-cover rounded border border-gold-300/10"
                  />
                ) : (
                  <div className="w-12 h-18 rounded border border-gold-300/10 bg-purple-900/20 flex items-center justify-center">
                    <Star className="w-4 h-4 text-purple-400" />
                  </div>
                )}
                <p className="text-xs text-[var(--text-muted)] mt-1 truncate w-12">{card.count}x</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default StatsWidget;
